/**
 * Floating widget button for the AI Assistant.
 */
import React, { useState } from 'react';
import { Badge, Button, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import ChatbotAvatar from '../avatars/DialoguinAvatarRedesign';
import { ChatPanel } from './ChatPanel';
import { useFloatingArea } from '../../../contexts/FloatingAreaContext.tsx';
import './AssistantWidget.css';

export interface AssistantWidgetProps {
  isStaff?: boolean;
  unreadCount?: number;
}

const BASE_BOTTOM = 24;
const BASE_RIGHT = 24;
const GAP = 16;

export const AssistantWidget: React.FC<AssistantWidgetProps> = ({
  isStaff = false,
  unreadCount = 0,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPromptDismissed, setIsPromptDismissed] = useState(false);
  const { panelHeight } = useFloatingArea();

  const computedBottom =
    panelHeight > 0
      ? `calc(var(--assistant-base-bottom, ${BASE_BOTTOM}px) + ${panelHeight}px + ${GAP}px)`
      : `var(--assistant-base-bottom, ${BASE_BOTTOM}px)`;

  const widgetStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: computedBottom,
    right: `var(--assistant-right, ${BASE_RIGHT}px)`,
  };

  const handleToggle = () => {
    setIsPanelOpen((current) => !current);
  };

  const assistantIcon = (
    <span className="assistant-widget__avatar">
      <ChatbotAvatar mood={isPanelOpen ? 'laughing' : 'happy'} />
    </span>
  );

  return (
    <>
      <div className="assistant-widget" style={widgetStyle}>
        {!isPanelOpen && !isPromptDismissed && (
          <div className="assistant-widget__prompt" aria-live="polite">
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setIsPromptDismissed(true)}
              aria-label="Cerrar sugerencia del asistente"
              className="assistant-widget__prompt-close"
            />
            <div className="assistant-widget__prompt-text">
              <strong>Hola, ¿te ayudo?</strong>
              <span>Consultame cómo usar las funciones disponibles.</span>
            </div>
            <div className="assistant-widget__prompt-actions">
              <Button
                type="text"
                size="small"
                onClick={() => setIsPanelOpen(true)}
              >
                Ver ayuda
              </Button>
              <Button
                type="text"
                size="small"
                onClick={() => setIsPanelOpen(true)}
              >
                Consultar
              </Button>
            </div>
          </div>
        )}

        <Tooltip title="Asistente IA" placement="left">
          <Badge
            count={unreadCount}
            offset={[-5, 5]}
            className="assistant-widget__badge"
          >
            <Button
              type="primary"
              shape="circle"
              icon={assistantIcon}
              onClick={handleToggle}
              size="large"
              aria-label="Abrir asistente IA"
              className="assistant-widget-button"
            />
          </Badge>
        </Tooltip>
      </div>

      <ChatPanel
        open={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        isStaff={isStaff}
      />
    </>
  );
};
